// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { TraitValueModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { UnorderedListProps } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Swatch, { SwatchTypeValues } from "@west/MobiusOverrides/Swatch";
import { mergeWithCss } from "@west/XDL/Utilities";
import React, { useState } from "react";
import styled, { css } from "styled-components";

interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
    // XNG-Changes
    extendedStyles?: ProductDetailsVariantGridStyles;
    showLabel?: boolean;
    showClear?: boolean;
}

export interface ProductDetailsVariantGridStyles {
    labelText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    labelWrapper?: InjectableCss;
    swatchClickable?: ClickablePresentationProps;
    swatchCaptionProps?: InjectableCss;
    // XNG-Changes
    traitValuesList?: UnorderedListProps;
    swatchGridWrapper?: InjectableCss;
}

export const variantGridStyles: ProductDetailsVariantGridStyles = {
    labelText: {
        css: css`
            font-size: 15px;
            font-weight: 600;
        `,
    },
    labelWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    swatchClickable: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    link: {
        css: css`
            margin: 10px 5px;
        `,
    },
    swatchCaptionProps: {
        css: css`
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            text-align: center;
        `,
    },
};

// XNG-Changes
const SwatchGridDisplayItem = styled.li`
    display: inline-block;
    text-align: center;
`;

// XNG-Changes
export const UnorderedList = styled.ul<InjectableCss>`
    overflow-y: auto;
    max-height: 300px;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 10px 4px;
    width: 100%;
    ${injectCss}
`;

const ProductDetailsVariantSwatchGrid = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    extendedStyles,
    showLabel = true,
    showClear = false,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(variantGridStyles, extendedStyles));
    const updateVariantSelectionSwatchGrid = (traitValueId?: string) => {
        updateVariantSelection(traitValueId ?? "", variantTrait.id);
    };

    const CreateSwatchGrid = (traitValue: TraitValueModel) => {
        return (
            <SwatchGridDisplayItem key={traitValue.id}>
                <Clickable
                    onClick={() => updateVariantSelectionSwatchGrid(traitValue.id)}
                    {...styles.swatchClickable}
                    disabled={traitValue.isDisabled}
                >
                    <Swatch
                        type={traitValue.swatchType as SwatchTypeValues}
                        value={
                            traitValue.swatchType === SwatchTypeValues.Image
                                ? traitValue.swatchImageValue
                                : traitValue.swatchColorValue
                        }
                        size={28}
                        caption={traitValue.valueDisplay}
                        displayTextWithSwatch={variantTrait.displayTextWithSwatch}
                        isDisabled={traitValue.isDisabled}
                        captionProps={styles.swatchCaptionProps}
                        isSelected={variantSelection[variantTrait.id] === traitValue.id}
                    />
                </Clickable>
            </SwatchGridDisplayItem>
        );
    };
    // XNG-Changes: extract selected traitVlaue
    const selectedTraitValue =
        variantTrait.traitValues?.find(traitValue => variantSelection[variantTrait.id] === traitValue.id)
            ?.valueDisplay || "";

    // XNG-Changes: displayed selected traitValue
    return (
        <StyledWrapper key={variantTrait.id} {...styles.swatchGridWrapper}>
            {/* {showLabel && showClear && ( */}
            <StyledWrapper {...styles.labelWrapper}>
                {showLabel && (
                    <Typography
                        {...styles.labelText}
                    >{`${variantTrait.nameDisplay}: ${selectedTraitValue}`}</Typography>
                )}
                {showClear && (
                    <Link
                        {...styles.link}
                        onClick={() => updateVariantSelectionSwatchGrid("")}
                        aria-label={translate("Clear Selection")}
                    >
                        {translate("Clear Selection")}
                    </Link>
                )}
            </StyledWrapper>
            {/* )} */}
            <UnorderedList {...styles.traitValuesList}>
                {variantTrait.traitValues?.map(traitValue => CreateSwatchGrid(traitValue))}
            </UnorderedList>
        </StyledWrapper>
    );
};
export default ProductDetailsVariantSwatchGrid;
